import { simulator } from "./simulator";
import { exitSandbox, getRole, sandboxUser, SANDBOX_ORG_ID } from "./session";

// Answers every same-origin /api request locally, with the same response
// shapes the backend returns. Anything outside /api (the app's own static
// files) goes to the network as usual. An /api path the sandbox does not
// know gets a 404 here; it is never forwarded to the real server.

const json = (body: unknown, status = 200) =>
  new Response(JSON.stringify(body), { status, headers: { "Content-Type": "application/json" } });

const SIMULATED = "This is simulated in the sandbox.";
const PAGE_SIZE = 20;

function isAdmin() {
  return getRole() === "admin";
}

async function readBody(init?: RequestInit): Promise<any> {
  if (!init?.body || typeof init.body !== "string") return {};
  try {
    return JSON.parse(init.body);
  } catch (_) {
    return {};
  }
}

function billingState() {
  const systems = simulator.allSystems();
  const full = {
    entitled: true,
    reason: "trialing",
    configured: true,
    enforcement: "enforce",
    status: "trialing",
    override: "none",
    overrideUntil: null,
    trialEndsAt: new Date(Date.now() + 21 * 86400000).toISOString(),
    currentPeriodEnd: new Date(Date.now() + 21 * 86400000).toISOString(),
    cancelAtPeriodEnd: false,
    hasSubscription: true,
    systemCount: systems.length,
    systemNames: systems.map((s) => s.system_name),
    monthlyCents: systems.length * 5000,
  };
  if (isAdmin()) return full;
  const { entitled, reason, configured, enforcement, systemNames, systemCount } = full;
  return { entitled, reason, configured, enforcement, systemNames, systemCount };
}

function patientStudies(name: string) {
  const logs = simulator.logs.filter((l) => l.patientname === name);
  if (logs.length === 0) return [];
  const first = new Date(logs[0].timestamp).getTime();
  const last = new Date(logs[logs.length - 1].timestamp).getTime();
  return [
    {
      studyIndex: 0,
      systemname: logs[logs.length - 1].systemname,
      start: logs[0].timestamp,
      end: logs[logs.length - 1].timestamp,
      effective_start: first,
      effective_end: last,
      check_count: logs.length,
      logs: logs.slice().reverse(),
      live_view_sessions: simulator.liveSessions
        .filter((v) => v.patientname === name && v.ended_at)
        .map(({ id, username, started_at, ended_at, duration_seconds }) => ({ id, username, started_at, ended_at, duration_seconds })),
      login_events: [],
    },
  ];
}

function auditData(start: number, end: number) {
  const inRange = (iso: string) => {
    const t = new Date(iso).getTime();
    return t >= start && t <= end;
  };
  return simulator.users.map((u) => {
    const checks = simulator.logs.filter((l) => l.user_id === u.sub && inRange(l.timestamp));
    const liveViews = simulator.liveSessions.filter((v) => v.user_id === u.sub && v.ended_at && inRange(v.started_at));
    // A gap of more than an hour between touches on the same patient is a missed check.
    const missedDetails: { patient: string; from: string; to: string; gapMinutes: number }[] = [];
    const byPatient = new Map<string, string[]>();
    checks.forEach((c) => byPatient.set(c.patientname, [...(byPatient.get(c.patientname) || []), c.timestamp]));
    byPatient.forEach((times, patient) => {
      for (let i = 1; i < times.length; i++) {
        const gap = (new Date(times[i]).getTime() - new Date(times[i - 1]).getTime()) / 60000;
        if (gap > 60) missedDetails.push({ patient, from: times[i - 1], to: times[i], gapMinutes: Math.round(gap) });
      }
    });
    return {
      user: { sub: u.sub, fullname: u.fullname, username: u.username, role: u.role },
      logins: simulator.logins.filter((l) => l.user_id === u.sub && inRange(l.logged_in_at)).map((l) => l.logged_in_at),
      checks: checks.map(({ timestamp, patientname, systemname, note, username }) => ({ timestamp, patientname, systemname, note, username })),
      liveViews: liveViews.map(({ started_at, ended_at, duration_seconds, patientname, systemname, username }) => ({ started_at, ended_at, duration_seconds, patientname, systemname, username })),
      missedChecks: missedDetails.length,
      missedDetails,
      totalLiveDuration: liveViews.reduce((a, v) => a + (v.duration_seconds || 0), 0),
    };
  });
}

async function route(url: URL, init?: RequestInit): Promise<Response> {
  simulator.ensureRole();
  const path = url.pathname;
  const method = (init?.method || "GET").toUpperCase();
  const role = getRole();
  const adminOnly = () => (isAdmin() ? null : json({ message: "Forbidden: Admins only" }, 403));

  // ── Session ──
  if (path === "/api/v1/verify-session") {
    return role ? json({ isAuthenticated: true, user: sandboxUser(role) }) : json({ isAuthenticated: false }, 401);
  }
  if (path === "/api/v1/logout") {
    exitSandbox();
    return new Response("Logged out successfully", { status: 200 });
  }
  if (path === "/api/refreshToken") {
    return json({ message: "Success", exp: Math.floor(Date.now() / 1000) + 12 * 3600 });
  }
  if (path === "/api/getOrganization") return json({ message: "FOUND_ORG", friendlyname: "EpicareAlert Sandbox" });
  if (!role) return json({ isAuthenticated: false }, 401);

  // ── Billing (display only; nothing can be bought here) ──
  if (path === "/api/v1/billing/status") return json(billingState());
  if (path === "/api/v1/billing/invoices") return adminOnly() || json([]);
  if (path.startsWith("/api/v1/billing/")) return json({ message: `Billing is not connected in the sandbox. ${SIMULATED}` }, 503);

  // ── Organization settings ──
  if (path === "/api/v1/organization-settings") {
    if (method === "GET") return json(simulator.settings);
    const denied = adminOnly();
    if (denied) return denied;
    const b = await readBody(init);
    const minutes = Number(b.check_reminder_minutes);
    const cap = Number(b.max_systems_per_user);
    if (!(minutes >= 1 && minutes <= 59)) return json({ error: "Minutes must be between 1 and 59" }, 400);
    if (!(cap >= 1 && cap <= 1000)) return json({ error: "System limit must be between 1 and 1000" }, 400);
    Object.assign(simulator.settings, {
      check_reminder_hours: Number(b.check_reminder_hours) || 0,
      check_reminder_minutes: minutes,
      send_missed_checks_to_admins: !!b.send_missed_checks_to_admins,
      send_text_reminders: !!b.send_text_reminders,
      max_systems_per_user: cap,
      updated_at: new Date().toISOString(),
    });
    return json(simulator.settings);
  }

  // ── Users ──
  if (path === "/api/v1/getUsersInOrg") return adminOnly() || json({ user: simulator.users });
  if (path === "/api/v1/createNewUser") {
    const denied = adminOnly();
    if (denied) return denied;
    const b = await readBody(init);
    if (simulator.users.some((u) => u.username === b.username)) return json({ message: "User account already exists" }, 409);
    const user = {
      sub: `u-${Date.now()}`,
      username: String(b.username || ""),
      fullname: String(b.fullname || ""),
      phonenumber: String(b.phonenumber || ""),
      organizationid: SANDBOX_ORG_ID,
      role: b.role === "admin" ? "admin" : "user",
      created_at: new Date().toISOString(),
      disabled_at: null,
    };
    simulator.users.push(user);
    return json(user, 201);
  }
  if (path === "/api/v1/admin/delete-user") {
    const denied = adminOnly();
    if (denied) return denied;
    const b = await readBody(init);
    if (b.userSub === simulator.me.sub) return json({ message: "You cannot delete your own account." }, 400);
    simulator.users = simulator.users.filter((u) => u.sub !== b.userSub);
    return json({ message: "Success" });
  }
  if (path === "/api/v1/admin/disable-user" || path === "/api/v1/admin/enable-user") {
    const denied = adminOnly();
    if (denied) return denied;
    const b = await readBody(init);
    const u = simulator.users.find((x) => x.sub === b.userSub);
    if (!u) return json({ message: "User not found" }, 404);
    if (u.sub === simulator.me.sub) return json({ message: "You cannot disable your own account." }, 400);
    u.disabled_at = path.endsWith("disable-user") ? new Date().toISOString() : null;
    return json({ message: "Success" });
  }
  if (path === "/api/v1/admin/reset-mfa") return adminOnly() || json({ message: "Success" });

  // ── Systems ──
  if (path === "/api/v1/getOrgSystems") return adminOnly() || json({ systems: simulator.orgSystems() });
  if (path === "/api/v1/createSystem") {
    const denied = adminOnly();
    if (denied) return denied;
    const b = await readBody(init);
    return json(
      {
        message: "System created. In the sandbox it installs itself and comes online in a few seconds.",
        system: simulator.createSystem(b.systemName),
        activationWindowMinutes: 10,
      },
      201,
    );
  }
  if (path === "/api/v1/deactivateSystem") {
    const denied = adminOnly();
    if (denied) return denied;
    const b = await readBody(init);
    return simulator.deactivateSystem(String(b.systemId)) ? json({ success: true }) : json({ error: "System not found" }, 404);
  }
  if (path === "/api/v1/forceUpdate") {
    return adminOnly() || json({ success: true, message: `Update triggered for ${simulator.systems.size} systems. ${SIMULATED}` });
  }
  if (path === "/api/v1/installer-info") return json({ error: `There is no installer to download in the sandbox.` }, 503);

  // ── Check logs ──
  if (path === "/api/v1/checklogs/patients") {
    const by = new Map<string, typeof simulator.logs>();
    simulator.logs.forEach((l) => by.set(l.patientname, [...(by.get(l.patientname) || []), l]));
    const patients = Array.from(by.entries()).map(([patientname, logs]) => ({
      patientname,
      total_checks: String(logs.length),
      last_check: logs[logs.length - 1].timestamp,
      systemname: logs[logs.length - 1].systemname,
      live_view_count: String(simulator.liveSessions.filter((v) => v.patientname === patientname).length),
    }));
    patients.sort((a, b) => b.last_check.localeCompare(a.last_check));
    return json({ patients });
  }
  if (path.startsWith("/api/v1/checklogs/patients/")) {
    const name = decodeURIComponent(path.slice("/api/v1/checklogs/patients/".length));
    return json({ patient: name, studies: patientStudies(name) });
  }
  if (path === "/api/v1/checklogs") {
    const search = (url.searchParams.get("search") || "").toLowerCase();
    const date = url.searchParams.get("date");
    const page = Math.max(1, Number(url.searchParams.get("page")) || 1);
    let logs = simulator.logs.slice().reverse();
    if (search) {
      logs = logs.filter((l) => [l.patientname, l.username, l.systemname, l.note].some((f) => f.toLowerCase().includes(search)));
    }
    if (date) logs = logs.filter((l) => l.timestamp.startsWith(date));
    return json({
      logs: logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
      totalPages: Math.max(1, Math.ceil(logs.length / PAGE_SIZE)),
    });
  }

  // ── Audit ──
  if (path === "/api/v1/auditlogs") {
    const denied = adminOnly();
    if (denied) return denied;
    const startParam = url.searchParams.get("start");
    const endParam = url.searchParams.get("end");
    const start = startParam ? new Date(startParam).getTime() : Date.now() - 7 * 86400000;
    const end = endParam ? new Date(endParam).getTime() : Date.now();
    return json({ auditData: auditData(start, end), startDate: new Date(start).toISOString(), endDate: new Date(end).toISOString() });
  }

  return json({ message: `Not available in the sandbox: ${path}` }, 404);
}

let installed = false;

/** Replace window.fetch so /api requests are answered by the sandbox. */
export function installMockApi() {
  if (installed) return;
  installed = true;
  const real = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    let url: URL;
    try {
      url = new URL(raw, window.location.origin);
    } catch (_) {
      return real(input, init);
    }
    if (url.origin === window.location.origin && url.pathname.startsWith("/api/")) {
      // A short delay so loading states behave as they do against a server.
      await new Promise((r) => setTimeout(r, 120));
      return route(url, init);
    }
    return real(input, init);
  };
}
